// Inline search suggestions under the header search field.
// Opens once the trimmed query reaches `min-chars` (default 3), using Shopify's
// predictive search JSON endpoint. Accessible combobox: arrow keys move through
// options, Enter opens the active one, Escape closes, Tab leaves.
class SHeaderSuggest extends HTMLElement {
  #input;
  #panel;
  #timer = 0;
  #controller = null;
  #active = -1;
  #lastQuery = '';

  connectedCallback() {
    this.#input = this.querySelector('input[name="q"]');
    this.#panel = this.querySelector('[data-suggest-panel]');
    if (!this.#input || !this.#panel) return;

    this.#input.setAttribute('role', 'combobox');
    this.#input.setAttribute('aria-autocomplete', 'list');
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.setAttribute('aria-controls', this.#panel.id);

    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('keydown', this.#onKeydown);
    this.#input.addEventListener('focus', this.#onFocus);
    document.addEventListener('pointerdown', this.#onOutside);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onOutside);
    window.clearTimeout(this.#timer);
    this.#controller?.abort();
  }

  get #minChars() {
    return Number(this.getAttribute('min-chars')) || 3;
  }

  #onInput = () => {
    window.clearTimeout(this.#timer);
    const query = this.#input.value.trim();
    if (query.length < this.#minChars) {
      this.#controller?.abort();
      this.#lastQuery = '';
      this.#close();
      return;
    }
    this.#timer = window.setTimeout(() => this.#fetch(query), 250);
  };

  #onFocus = () => {
    if (this.#input.value.trim().length >= this.#minChars && this.#panel.childElementCount) this.#open();
  };

  #onOutside = (event) => {
    if (!this.contains(event.target)) this.#close();
  };

  #onFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.#close();
  };

  #onKeydown = (event) => {
    const options = this.#options();
    if (event.key === 'Escape') {
      if (!this.#panel.hidden) {
        event.preventDefault();
        this.#close();
      }
      return;
    }
    if (!options.length || this.#panel.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#setActive((this.#active + step + options.length) % options.length);
    } else if (event.key === 'Enter' && this.#active >= 0) {
      event.preventDefault();
      options[this.#active].querySelector('a')?.click();
    }
  };

  async #fetch(query) {
    if (query === this.#lastQuery) {
      this.#open();
      return;
    }
    this.#controller?.abort();
    this.#controller = new AbortController();
    const url = new URL(`${this.dataset.endpoint}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: this.#controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      this.#lastQuery = query;
      this.#render(query, data?.resources?.results || {});
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#close();
    }
  }

  #render(query, results) {
    const queries = (results.queries || []).slice(0, 4);
    const collections = (results.collections || []).slice(0, 3);
    const products = (results.products || []).slice(0, 6);
    const t = this.dataset;
    const parts = [];
    let index = 0;
    const optionId = () => `${this.#panel.id}-opt-${index++}`;

    if (queries.length) {
      parts.push(this.#group(t.labelSuggestions, queries.map((item) => `
        <li role="option" id="${optionId()}" aria-selected="false" class="rounded-button">
          <a href="${this.#escape(item.url)}" tabindex="-1" class="flex min-h-11 items-center gap-3 rounded-button px-3 text-sm text-on-surface no-underline hover:bg-surface-secondary">
            <span class="inline-flex size-4 shrink-0 items-center justify-center text-on-surface-secondary" aria-hidden="true">${this.#searchIcon}</span>
            <span>${item.styled_text || this.#escape(item.text)}</span>
          </a>
        </li>`)));
    }

    if (collections.length) {
      parts.push(this.#group(t.labelCollections, collections.map((item) => `
        <li role="option" id="${optionId()}" aria-selected="false" class="rounded-button">
          <a href="${this.#escape(item.url)}" tabindex="-1" class="flex min-h-11 items-center rounded-button px-3 text-sm text-on-surface no-underline hover:bg-surface-secondary">${this.#escape(item.title)}</a>
        </li>`)));
    }

    if (products.length) {
      parts.push(this.#group(t.labelProducts, products.map((item) => {
        const image = item.featured_image?.url || item.image || '';
        const src = image ? `${image}${image.includes('?') ? '&' : '?'}width=96` : '';
        const unavailable = item.available === false ? ` <span class="text-xs text-on-surface-secondary">· ${this.#escape(t.labelUnavailable)}</span>` : '';
        return `
        <li role="option" id="${optionId()}" aria-selected="false" class="rounded-button">
          <a href="${this.#escape(item.url)}" tabindex="-1" class="flex min-h-14 items-center gap-3 rounded-button px-3 py-1.5 no-underline hover:bg-surface-secondary">
            <span class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-surface">${src ? `<img src="${this.#escape(src)}" alt="" width="44" height="44" loading="lazy" class="size-full object-contain">` : ''}</span>
            <span class="flex min-w-0 flex-col">
              <span class="line-clamp-2 text-sm text-on-surface">${this.#escape(item.title)}</span>
              <span class="text-sm font-semibold text-on-surface">${this.#escape(this.#money(item.price))}${unavailable}</span>
            </span>
          </a>
        </li>`;
      })));
    }

    const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
    const viewAll = `
      <div class="border-t border-border p-2">
        <a href="${this.#escape(searchUrl)}" class="flex min-h-11 items-center justify-center rounded-button px-3 text-sm font-medium text-on-surface underline underline-offset-4 hover:bg-surface-secondary">
          ${this.#escape(t.labelViewAll.replace('__QUERY__', query))}
        </a>
      </div>`;

    if (!parts.length) {
      this.#panel.innerHTML = `<p class="px-4 py-4 text-sm text-on-surface-secondary">${this.#escape(t.labelEmpty.replace('__QUERY__', query))}</p>`;
    } else {
      this.#panel.innerHTML = `<div class="max-h-[70vh] overflow-y-auto p-2">${parts.join('')}</div>${viewAll}`;
    }

    this.#active = -1;
    this.#input.removeAttribute('aria-activedescendant');
    this.#open();

    const live = this.querySelector('[data-suggest-status]');
    if (live) {
      const count = queries.length + collections.length + products.length;
      live.textContent = count ? t.labelCount.replace('__COUNT__', String(count)) : t.labelEmpty.replace('__QUERY__', query);
    }
  }

  #group(label, items) {
    return `<div role="group" aria-label="${this.#escape(label)}" class="py-1">
      <p class="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-on-surface-secondary" aria-hidden="true">${this.#escape(label)}</p>
      <ul role="presentation" class="flex flex-col">${items.join('')}</ul>
    </div>`;
  }

  #options() {
    return [...this.#panel.querySelectorAll('[role="option"]')];
  }

  #setActive(next) {
    const options = this.#options();
    options.forEach((option, i) => {
      const selected = i === next;
      option.setAttribute('aria-selected', String(selected));
      option.classList.toggle('bg-surface-secondary', selected);
    });
    this.#active = next;
    const current = options[next];
    if (current) {
      this.#input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  #open() {
    this.#panel.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.#panel.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.removeAttribute('aria-activedescendant');
    this.#active = -1;
  }

  #money(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const format = this.dataset.moneyFormat || '{{amount_no_decimals}}';
    const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const whole = Math.round(amount).toString();
    const fixed = amount.toFixed(2);
    const [int, dec] = fixed.split('.');
    const replacements = {
      amount: `${group(int, ',')}.${dec}`,
      amount_no_decimals: group(whole, ','),
      amount_with_comma_separator: `${group(int, '.')},${dec}`,
      amount_no_decimals_with_comma_separator: group(whole, '.'),
      amount_with_space_separator: `${group(int, ' ')},${dec}`,
      amount_no_decimals_with_space_separator: group(whole, ' '),
      amount_with_apostrophe_separator: `${group(int, "'")}.${dec}`,
    };
    const text = format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => replacements[key] ?? match);
    const el = document.createElement('span');
    el.innerHTML = text;
    return el.textContent || '';
  }

  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  get #searchIcon() {
    return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
  }
}

if (!customElements.get('s-header-suggest')) {
  customElements.define('s-header-suggest', SHeaderSuggest);
}
